import { AlignmentTransform } from "@/modes/alignment-modes-commons/alignment-transform";
import { TwoPointsPairsAlignmentAnchorPositions } from "@/modes/alignment-modes-commons/compute-split-screen-alignment";
import { GUID } from "@faro-lotv/foundation";
import { clearStore } from "@faro-lotv/project-source";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Vector3Tuple } from "three";
import { AlignmentViewLayout, SplitDirection } from "./alignment-ui-types";

/* reference that is used for alignment, to support both cloud-cad and cad-to-cloud alignment */
export enum AlignmentReference {
  bimModel = "bimModel",
  pointCloud = "pointCloud",
}

/** This defines all the steps to align cloud and a 3D model */
export enum CloudCadAlignmentStep {
  setElevations = "setElevations",
  alignIn2d = "alignIn2d",
}

/** State managing data exchange during cloud and cad alignment session */
export type CloudCadAlignmentToolState = {
  /** reference type for cloud and cad alignment */
  alignmentReference: AlignmentReference;

  /** reference to cloud ID used during current alignment session */
  cloudIdForAlignment?: GUID;

  /** level elevation in 3D model */
  modelElevation: number;

  /** level elevation in point cloud */
  cloudElevation: number;

  /** Alignment step */
  step: CloudCadAlignmentStep;

  /** if true clipping box in Vertical alignment step is enabled */
  isModelClippingBoxEnabled: boolean;

  /** if true, extract cross section at elevation defined on first step for cad model tomographic view */
  isModelCrossSectionEnabled: boolean;

  /** if true, extract cross section at elevation defined on first step for cloud tomographic view */
  isCloudCrossSectionEnabled: boolean;

  /**
   * Incremental transform of the cloud to cad model (in case alignmentReference is "bimModel") OR
   * cad model to cloud (in case alignmentReference is "pointCloud"). Translations are in meters.
   * At the end of each alignment cycle it must be reset to undefined to prevent reusing it in next session of alignment
   */
  incrementalTransform?: AlignmentTransform;

  /**
   * anchor positions defined in split screen alignment
   * In case that alignmentReference is "bimModel", reference anchors are from cad model and moving anchors are from point cloud.
   * In case that alignmentReference is "pointCloud", reference anchors are from point cloud and moving anchors are from cad model.
   */
  alignmentAnchorPositions: TwoPointsPairsAlignmentAnchorPositions;

  /** cloud-CAD alignment screen layout */
  alignmentLayout: AlignmentViewLayout;

  /** the alignment screen split direction */
  splitDirection: SplitDirection;
};

const initialState: CloudCadAlignmentToolState = {
  alignmentReference: AlignmentReference.bimModel,
  step: CloudCadAlignmentStep.setElevations,
  isModelClippingBoxEnabled: false,
  alignmentAnchorPositions: {},
  isModelCrossSectionEnabled: true,
  isCloudCrossSectionEnabled: true,
  alignmentLayout: AlignmentViewLayout.splitScreen,
  splitDirection: SplitDirection.horizontalSplit,
  modelElevation: 0,
  cloudElevation: 0,
};

const cloudCadAlignmentModeSlice = createSlice({
  initialState,
  name: "cloudCadAlignmentMode",
  reducers: {
    setAlignmentReference(state, action: PayloadAction<AlignmentReference>) {
      state.alignmentReference = action.payload;
    },
    setStepForCloudCadAlignment(
      state,
      action: PayloadAction<CloudCadAlignmentStep>,
    ) {
      state.step = action.payload;
    },

    setModelClippingBoxEnabled(state, action: PayloadAction<boolean>) {
      state.isModelClippingBoxEnabled = action.payload;
    },

    setModelCrossSectionEnabled(state, action: PayloadAction<boolean>) {
      state.isModelCrossSectionEnabled = action.payload;
    },

    setCloudCrossSectionEnabled(state, action: PayloadAction<boolean>) {
      state.isCloudCrossSectionEnabled = action.payload;
    },

    setCloudForCloudCadAlignment(state, action: PayloadAction<GUID>) {
      state.cloudIdForAlignment = action.payload;
    },

    setModelElevationForCloudCadAlignment(
      state,
      action: PayloadAction<number>,
    ) {
      state.modelElevation = action.payload;
    },

    setCloudElevationForCloudCadAlignment(
      state,
      action: PayloadAction<number>,
    ) {
      state.cloudElevation = action.payload;
    },

    setCloudCadIncrementalTransform(
      state,
      action: PayloadAction<AlignmentTransform | undefined>,
    ) {
      state.incrementalTransform = action.payload;
    },

    setMovingElementAnchor1ForCloudAlignment(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.movingElementAnchor1 = action.payload;
    },

    setMovingElementAnchor2ForCloudAlignment(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.movingElementAnchor2 = action.payload;
    },

    setReferenceElementAnchor1ForCloudAlignment(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.referenceElementAnchor1 = action.payload;
    },

    setReferenceElementAnchor2ForCloudAlignment(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.referenceElementAnchor2 = action.payload;
    },

    resetCloudCadAlignment(state) {
      state.alignmentReference = AlignmentReference.bimModel;
      state.step = CloudCadAlignmentStep.setElevations;
      state.cloudIdForAlignment = undefined;
      state.modelElevation = 0;
      state.cloudElevation = 0;
      state.isModelClippingBoxEnabled = false;
      state.incrementalTransform = undefined;
      state.alignmentAnchorPositions = {};
      state.alignmentLayout = AlignmentViewLayout.splitScreen;
      state.splitDirection = SplitDirection.horizontalSplit;
    },

    setCloudCadAlignmentLayout(
      state,
      action: PayloadAction<AlignmentViewLayout>,
    ) {
      state.alignmentLayout = action.payload;
    },

    setCloudCadAlignmentSplitDirection(
      state,
      action: PayloadAction<SplitDirection>,
    ) {
      state.splitDirection = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const {
  setAlignmentReference,
  resetCloudCadAlignment,
  setStepForCloudCadAlignment,
  setCloudForCloudCadAlignment,
  setCloudCadIncrementalTransform,
  setModelElevationForCloudCadAlignment,
  setCloudElevationForCloudCadAlignment,
  setModelClippingBoxEnabled,
  setModelCrossSectionEnabled,
  setCloudCrossSectionEnabled,
  setCloudCadAlignmentLayout,
  setMovingElementAnchor1ForCloudAlignment,
  setMovingElementAnchor2ForCloudAlignment,
  setReferenceElementAnchor1ForCloudAlignment,
  setReferenceElementAnchor2ForCloudAlignment,
  setCloudCadAlignmentSplitDirection,
} = cloudCadAlignmentModeSlice.actions;

export const cloudCadAlignmentModeReducer = cloudCadAlignmentModeSlice.reducer;
